/**
 * Le niveau du compte.
 *
 * Les points de trophee restent locaux : ce sont eux qui font les
 * classements de chaque ville, et rien ici ne les touche. Le niveau, lui,
 * est attache au compte et se lit sur la somme de tous les serveurs.
 *
 * Tout ce fichier est du calcul pur, sans acces a la base. C'est
 * volontaire : l'application et le script de migration doivent trouver le
 * meme niveau pour les memes points, et un calcul recopie a deux endroits
 * finit toujours par diverger.
 */
/** Cout d'un niveau, constant quel que soit le niveau. */
export const LEVEL_STEP = 200;
/** Plafond. Au-dela, les points continuent de s'accumuler. */
export const MAX_LEVEL = 400;
/**
 * La couleur du medaillon, par niveau d'entree croissant.
 *
 * Il n'y a plus de titre : le niveau se lit sur le chiffre, et la couleur
 * dit d'un coup d'oeil ou l'on en est. `from` est inclusif ; une couleur
 * court jusqu'au `from` de la suivante. Pas de borne haute stockee — elle
 * se deduit, et deux bornes a tenir d'accord finissent par se contredire.
 *
 * Ces couleurs sont en dur : elles disent un rang, pas une ville, et ne
 * suivent donc pas le theme du spot.
 */
const LEVEL_COLORS = [
    { from: 1, color: '#4ADE80' },
    { from: 25, color: '#3B82F6' },
    { from: 70, color: '#A855F7' },
    { from: 140, color: '#F97316' },
    { from: 250, color: '#EF4444' },
];
/**
 * Le niveau que valent ces points.
 *
 * Division entiere, sans racine ni flottant : le cout d'un niveau ne
 * change pas, donc un total se convertit d'un seul quotient.
 */
export function levelForPoints(totalPoints) {
    // Un total absent ou aberrant vaut le premier niveau, jamais zero.
    if (!Number.isFinite(totalPoints) || totalPoints <= 0)
        return 1;
    return Math.min(Math.floor(totalPoints / LEVEL_STEP) + 1, MAX_LEVEL);
}
/** La couleur d'un niveau. Jamais stockee : elle se derive. */
export function colorForLevel(level) {
    let found = LEVEL_COLORS[0];
    for (const step of LEVEL_COLORS) {
        if (level >= step.from)
            found = step;
    }
    return found.color;
}
/** Le niveau d'un membre, a partir de ses points serveur par serveur. */
export function levelSummary(servers) {
    const totalPoints = servers.reduce((sum, server) => sum + Math.max(0, server.points || 0), 0);
    const level = levelForPoints(totalPoints);
    const capped = level >= MAX_LEVEL;
    /*
      Au plafond, le reste de la division retomberait a zero tous les 200
      points et ferait clignoter une barre pleine. On la fige pleine : il
      n'y a plus de niveau suivant a atteindre.
    */
    const pointsIntoLevel = capped ? LEVEL_STEP : totalPoints % LEVEL_STEP;
    return {
        totalPoints,
        level,
        color: colorForLevel(level),
        pointsIntoLevel,
        pointsForNextLevel: capped ? null : LEVEL_STEP,
        percent: pointsIntoLevel / (LEVEL_STEP / 100),
        servers: [...servers].sort((a, b) => b.points - a.points),
    };
}
/**
 * La montee de niveau entre deux totaux, s'il y en a une.
 *
 * Un seul saut, meme quand il traverse plusieurs niveaux. Avec un cout
 * fixe de 200 points, franchir quatre niveaux d'un coup n'a rien
 * d'exceptionnel — un trophee a 750 points suffit. Emettre un evenement
 * par niveau donnerait quatre animations a la suite pour un seul geste.
 */
export function levelUpBetween(userId, before, after) {
    const from = levelForPoints(before);
    const to = levelForPoints(after);
    if (to <= from)
        return null;
    return {
        userId,
        from,
        to,
        colorChanged: colorForLevel(from) !== colorForLevel(to),
    };
}
